import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUnitProvider } from '/@/core/context/unit';
import { useMutateCreateOrUpdateCropMonitoring } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { plantedAreaForForm } from '/@/hooks/geometry/area';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import NewCropMonitoringFormModal from '../../../../creation-dialog/components/NewCropMonitoringFormModal';
import ExitWithoutSavingModal from '../../../../creation-dialog/ExitWithoutSavingModal';
import type { CropMonitoringFormType } from '../../../../creation-dialog/types';
import {
    errorDetail,
    flowEventProps,
    plantingPrefillEdited,
    trackFormAbandoned,
    useFieldRegistryFlow,
    useFormExitTracking,
} from './flow-tracking';
import type { RegistryAddStep } from './types';

const CreateRegistryPlanting = ({
    open,
    onClose,
    onSuccess,
    setStep,
    fieldId,
    cropMonitoring: cropMonitoringProp,
    initialValues,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    setStep?: (step: RegistryAddStep) => void;
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    /** Prefill for the "Registrar lo aplicado" flow — seeds the form from a recommendation. */
    initialValues?: Partial<CropMonitoringFormType>;
}) => {
    const fieldQuery = useFieldById({ id: fieldId ?? 0 }, { enabled: !!fieldId });
    const field = fieldQuery.data;

    let cropMonitoring = cropMonitoringProp ?? field?.activeCropMonitoring;
    if (cropMonitoring?.harvestDate) {
        cropMonitoring = undefined;
    }

    const [showExitWarning, setShowExitWarning] = useState<boolean>(false);
    const units = useUnitProvider();

    // This registry flow tracks a single planting, but the shared modal and HybridBlock
    // read/write `hybrids`, so the form must use that structure. The legacy flat prefill
    // (`initialValues` from the recommendation flow) is mapped into the single hybrid.
    const initialHybrid = {
        id: cropMonitoring?.id,
        plantingDate: initialValues?.plantingDate ?? cropMonitoring?.plantingDate,
        product: initialValues?.product ?? cropMonitoring?.cornCropMonitoring?.product ?? cropMonitoring?.productName,
        technology: cropMonitoring?.cornCropMonitoring?.technology,
        environment: initialValues?.environment ?? cropMonitoring?.environment,
        plantedArea: plantedAreaForForm(units, {
            reportHa: cropMonitoring?.plantedArea,
            fieldM2: field?.area,
        }),
        density: initialValues?.density,
    };

    const form = useForm<CropMonitoringFormType>({
        defaultValues: {
            fieldId: initialValues?.fieldId ?? cropMonitoring?.field ?? field?.id,
            id: cropMonitoring?.id,
            crop: initialValues?.crop ?? cropMonitoring?.crop ?? '',
            previousCrop: cropMonitoring?.previousCrop || '',
            hybrids: [initialHybrid],
        },
    });

    // `initialValues` only come from a recommendation card's "Registrar lo aplicado".
    const addCropEntrySource = initialValues ? 'recommendation' : 'field-detail';
    // biome-ignore lint/correctness/useExhaustiveDependencies: reported once per open, wherever it was opened from
    useEffect(() => {
        if (!open) return;
        mpTrackEvent('Add Crop Flow create began', { entry_source: addCropEntrySource });
    }, [open]);

    const { fieldId: formFieldId, crop, previousCrop, hybrids } = form.watch();

    // Only inside a flow: "Agregar registro" or a recommendation card.
    const flow = useFieldRegistryFlow();
    // What the recommendation brought and the user may have changed: the hybrid and its density.
    const prefillEdited = () => (initialValues ? plantingPrefillEdited(initialValues, hybrids?.[0]) : false);
    // Read while rendering, so the form keeps it up to date for the exit report.
    const { isDirty } = form.formState;
    const { end, finish } = useFormExitTracking((exitMethod) => {
        if (flow) trackFormAbandoned(flow, 'planting', exitMethod, isDirty || prefillEdited());
    });

    const { trigger: createOrUpdateCropMonitoring, isMutating } = useMutateCreateOrUpdateCropMonitoring(
        {
            hybrids,
            field: field?.id || formFieldId || 0,
            crop: crop || undefined,
            previousCrop: previousCrop === 'none' ? undefined : previousCrop || undefined,
            id: cropMonitoring?.id,
        },
        {
            onSuccess: () => {
                finish();
                if (flow)
                    mpTrackEvent('Field Registry Saved', {
                        ...flowEventProps(flow),
                        registry_type: 'planting',
                        origin: flow.origin,
                        entry_source: flow.entrySource,
                        ...(flow.origin === 'recommendation' ? { prefill_edited: prefillEdited() } : {}),
                    });
                form.reset();
                onSuccess();
            },
        }
    );

    const handleSubmit = async () => {
        try {
            await createOrUpdateCropMonitoring();
        } catch (error) {
            // The mutation already tells the user it failed.
            if (flow)
                mpTrackEvent('Field Registry Save Failed', {
                    ...flowEventProps(flow),
                    registry_type: 'planting',
                    origin: flow.origin,
                    error_detail: errorDetail(error),
                    // All of the campaign's hybrids go in one transaction
                    partial: false,
                });
        }
    };

    const handleInvalidSubmit = (invalidFields: string[]) => {
        if (flow)
            mpTrackEvent('Field Registry Submit Attempted', {
                ...flowEventProps(flow),
                registry_type: 'planting',
                invalid_fields: invalidFields,
            });
    };

    const handleClose = () => {
        if (isMutating) return;
        const firstHybrid = hybrids?.[0];
        const hasData =
            crop ||
            previousCrop ||
            !!(
                firstHybrid?.plantingDate ||
                firstHybrid?.environment ||
                firstHybrid?.plantedArea ||
                firstHybrid?.density ||
                firstHybrid?.product ||
                firstHybrid?.technology
            );
        if (hasData) {
            // Not left yet: the user may still go back to the form from the warning.
            setShowExitWarning(true);
        } else {
            end('close');
            onClose();
            form.reset();
        }
    };

    const handleDiscardAndExit = () => {
        end('close');
        setShowExitWarning(false);
        onClose();
        form.reset();
    };

    if (!open) return null;

    return (
        <>
            <NewCropMonitoringFormModal
                open={open && !showExitWarning}
                setClose={handleClose}
                goBack={
                    setStep
                        ? () => {
                              // Back to the type picker: the attempt goes on there.
                              finish();
                              setStep('select');
                          }
                        : undefined
                }
                form={form}
                field={field}
                onSubmit={handleSubmit}
                submitting={isMutating}
                onlyEdit={false}
                entry_source={addCropEntrySource}
                onInvalidSubmit={handleInvalidSubmit}
            />
            <ExitWithoutSavingModal
                open={showExitWarning}
                setClose={() => setShowExitWarning(false)}
                onDiscard={handleDiscardAndExit}
            />
        </>
    );
};

export default CreateRegistryPlanting;
